import { SQL } from '@/lib/Sqlite'
import { syncQueue, JobStatus } from './index'
import { normalizeFieldUpdates } from './jobGuards'

// 병합 저장 가드가 요구하는 row 최소 형태
interface MergedRowLike {
  id: number | string
  localStringId?: string | number
  modifiedFromLocal?: boolean | number
}

// 저장 직전 다시 읽은 현재 DB row 상태
export interface FreshRowState {
  modifiedFromLocal: number
  fieldsUpdatedAt: unknown
}

// 병합 스냅샷 대비 현재 row가 달라진 사유
export type SnapshotStaleReason = 'modified-during-merge' | 'stamps-changed'

/**
 * 병합 저장 직전, 해당 row의 현재 DB 상태(modifiedFromLocal, fields_updated_at)를 1회 다시 읽는다.
 * @param storeKey 테이블 이름
 * @param localStringId 대상 row의 localStringId (없으면 id로 조회)
 * @param id 대상 row의 id
 * @returns row가 없으면 null
 */
export async function readFreshRowState(
  storeKey: string,
  localStringId: string,
  id: string,
): Promise<FreshRowState | null> {
  const db = await SQL.getDatabase()
  const columns = ['modifiedFromLocal', 'fields_updated_at']
  const result = localStringId
    ? await db.query(`SELECT ${columns.join(', ')} FROM ${storeKey} WHERE localStringId = ?`, [
        localStringId,
      ])
    : await db.query(`SELECT ${columns.join(', ')} FROM ${storeKey} WHERE id = ?`, [id])
  const row = result.values?.[0] as Record<string, unknown> | undefined
  if (!row) return null
  return {
    modifiedFromLocal: Number(row.modifiedFromLocal ?? 0),
    fieldsUpdatedAt: row.fields_updated_at,
  }
}

/**
 * 병합 시작 시점 스냅샷과 현재 row를 비교해, 병합 도중 사용자 수정이 끼어들었는지 판정한다.
 * 스냅샷 기반 INSERT OR REPLACE는 그 사이의 수정(대표사진 변경 등)을 옛 값으로 되돌리고
 * modifiedFromLocal까지 0으로 내려 update job이 "이미 동기화됨"으로 스킵되게 하므로,
 * 달라진 row는 이번 사이클에 저장하지 않는다 (다음 사이클이 새 스냅샷으로 처리).
 * @param snapshot 병합에 사용한 로컬 스냅샷 row
 * @param fresh 저장 직전 다시 읽은 현재 row
 * @returns 끼어든 수정이 있으면 사유, 없으면 null
 */
export function getSnapshotStaleReason(
  snapshot: { modifiedFromLocal?: unknown; fields_updated_at?: unknown },
  fresh: FreshRowState,
): SnapshotStaleReason | null {
  const snapshotModified = Number(snapshot.modifiedFromLocal ?? 0) === 1
  if (fresh.modifiedFromLocal === 1 && !snapshotModified) return 'modified-during-merge'
  if (
    normalizeFieldUpdates(fresh.fieldsUpdatedAt) !==
    normalizeFieldUpdates(snapshot.fields_updated_at)
  ) {
    return 'stamps-changed'
  }
  return null
}

/**
 * 병합 결과 저장 직전, 병합 도중 업로드 job이 이미 교체/점유한 row의 옛 스냅샷을 걸러낸다.
 *
 * 병합은 시작 시점의 로컬 스냅샷을 사용하므로, 병합 중 업로드가 완료되어 id가
 * 서버 id로 교체된 row를 스냅샷 그대로 재기록하면 _need_to_upload 유령 row가 부활해
 * 재업로드(서버 중복 레코드)를 일으키고, 서버형 항목이 같은 PK(id)를 덮어쓰면
 * localShapeId 등 로컬 연결 정보가 파괴된다.
 *
 * ⚠️ initData 등 병합 결과 저장 경로 전용 — 사용자 수정 저장 경로(requestUpdate 일반 호출)에
 * 사용하면 연속 수정이 걸러질 수 있으므로 사용 금지.
 * @param itemsToProcess 저장 예정 항목
 * @param storeKey 테이블 이름
 * @param taskId 현재 task ID
 * @param logLabel 로그 식별용 라벨 (기본값: storeKey)
 * @param snapshotFieldUpdates 병합 스냅샷의 localStringId별 정규화 스탬프(normalizeFieldUpdates).
 *   주어지면 저장 직전 현재 row의 스탬프와 비교해, 병합 창 사이에 수정된 row는 저장에서 제외한다
 *   (SyncManager 경로의 getSnapshotStaleReason 'stamps-changed'와 동일 규칙)
 * @returns 안전하게 저장 가능한 항목만 남긴 목록
 */
export async function filterStaleMergedItems<T extends MergedRowLike>(
  itemsToProcess: { item: T; itemId: string }[],
  storeKey: string,
  taskId: string,
  logLabel: string = storeKey,
  snapshotFieldUpdates?: Map<string, string>,
): Promise<{ item: T; itemId: string }[]> {
  if (itemsToProcess.length === 0) return itemsToProcess

  const db = await SQL.getDatabase()
  const result = await db.query(
    `SELECT localStringId, id, modifiedFromLocal, fields_updated_at FROM ${storeKey} WHERE task_id = ?`,
    [taskId],
  )
  const currentIdByLocalStringId = new Map<string, string>()
  const currentLocalStringIdById = new Map<string, string>()
  // 현재 DB에서 미동기화(modifiedFromLocal=1)인 row - 병합 도중 사용자 수정이 들어온 row 보호용
  const currentModifiedLocalStringIds = new Set<string>()
  // 현재 DB row의 정규화 스탬프 - 스냅샷 이후 수정 여부 판정용
  const currentFieldUpdatesByLocalStringId = new Map<string, string>()
  for (const row of (result.values ?? []) as Array<{
    localStringId?: string
    id?: unknown
    modifiedFromLocal?: unknown
    fields_updated_at?: unknown
  }>) {
    if (row.localStringId) {
      currentIdByLocalStringId.set(String(row.localStringId), String(row.id))
      currentLocalStringIdById.set(String(row.id), String(row.localStringId))
      if (Number(row.modifiedFromLocal ?? 0) === 1) {
        currentModifiedLocalStringIds.add(String(row.localStringId))
      }
      if (snapshotFieldUpdates) {
        currentFieldUpdatesByLocalStringId.set(
          String(row.localStringId),
          normalizeFieldUpdates(row.fields_updated_at),
        )
      }
    }
  }

  const activeJobIds = new Set(
    syncQueue
      .getQueueState()
      .filter(job => job.status === JobStatus.PENDING || job.status === JobStatus.PROCESSING)
      .map(job => job.localStringId),
  )

  return itemsToProcess.filter(({ item }) => {
    const currentId = currentIdByLocalStringId.get(String(item.localStringId))
    // 병합 중 업로드 완료로 id가 교체된 row: 스냅샷을 다시 쓰면 유령 row가 생기므로 스킵
    if (currentId !== undefined && currentId !== String(item.id)) {
      console.warn(
        `[PhotoGuard] ${logLabel}: 병합 중 교체된 row 저장 스킵 (localStringId=${item.localStringId}, snapshot id=${item.id}, current id=${currentId})`,
      )
      return false
    }
    // PK(id)가 이미 다른 localStringId의 로컬 row로 점유된 경우(업로드 완료로 id 교체됨):
    // 서버형 항목으로 덮어쓰면 localShapeId 등 로컬 연결 정보가 파괴되므로 스킵
    const occupiedBy = currentLocalStringIdById.get(String(item.id))
    if (occupiedBy !== undefined && occupiedBy !== String(item.localStringId)) {
      console.warn(
        `[PhotoGuard] ${logLabel}: id-매핑된 row 보호 - 서버형 항목 저장 스킵 (id=${item.id}, 기존 localStringId=${occupiedBy})`,
      )
      return false
    }
    // 업로드 진행 중인 항목도 병합 결과(스냅샷 기반)로 덮어쓰지 않음
    if (item.modifiedFromLocal && activeJobIds.has(String(item.localStringId))) {
      return false
    }
    // 병합 도중 사용자 수정이 들어와 현재 DB는 dirty인데 병합 결과는 clean인 row:
    // 스냅샷으로 덮어쓰면 수정이 되돌아가고 update job이 스킵되므로 이번 저장에서 제외
    if (!item.modifiedFromLocal && currentModifiedLocalStringIds.has(String(item.localStringId))) {
      console.warn(
        `[PhotoGuard] ${logLabel}: 병합 중 수정된 row 저장 스킵 (localStringId=${item.localStringId})`,
      )
      return false
    }
    // 스냅샷 이후 스탬프가 바뀐 row(병합 창 사이의 풍선 이동 등): 스냅샷으로 되쓰면 수정이 유실되므로 제외
    if (snapshotFieldUpdates) {
      const snapshot = snapshotFieldUpdates.get(String(item.localStringId))
      const current = currentFieldUpdatesByLocalStringId.get(String(item.localStringId))
      if (snapshot !== undefined && current !== undefined && snapshot !== current) {
        console.warn(
          `[PhotoGuard] ${logLabel}: 병합 중 스탬프 변경된 row 저장 스킵 (localStringId=${item.localStringId}, stamps-changed)`,
        )
        return false
      }
    }
    return true
  })
}
